// Round-recap GIF generator. When a hiding pod is caught, we celebrate the
// seekers' progress with a downloadable animated GIF: each squad's board
// replays its eliminations in order (station dots greying out), with the squad
// members' names overlaid, an optional GPS trail (pin + connecting line) if
// positions were captured, and an ending card naming who caught the pod.
//
// Fully client-side: a stylized 2D canvas (the station dots ARE the map of
// Manhattan) encoded with gifenc. No MapLibre, no network.
import { GIFEncoder, quantize, applyPalette } from "gifenc";
import { STATIONS, STATION_BY_ID } from "./stations";
import { photoUrl, getToken, API_BASE } from "./api";
import type { GameClient } from "./net";

// The render is triggered through the worker (which sign-invokes the trigger Lambda).
export const RENDER_API = API_BASE;

// Trigger a Remotion Lambda render of THIS seeker's squad recap + poll to completion.
export async function renderHdVideo(client: GameClient, theme: string, onProgress?: (pct: number) => void): Promise<string> {
  const data = await buildRemotionRecap(client);
  if (!data || !data.moments.length) throw new Error("No run to recap yet — eliminations/clues/photos drive the video.");
  const token = getToken() ?? "";
  const start = await fetch(`${API_BASE}/recap/render`, { method: "POST", headers: { authorization: `Bearer ${token}`, "content-type": "application/json" }, body: JSON.stringify({ data, theme }) });
  const sj = await start.json().catch(() => ({}));
  if (!start.ok) throw new Error(sj.error || "Couldn't start the render.");
  const { renderId, bucketName } = sj as { renderId: string; bucketName: string };
  for (let i = 0; i < 150; i++) {
    await new Promise((r) => setTimeout(r, 2000));
    const st = await (await fetch(`${API_BASE}/recap/status?renderId=${encodeURIComponent(renderId)}&bucketName=${encodeURIComponent(bucketName)}`, { headers: { authorization: `Bearer ${token}` } })).json();
    if (st.error) throw new Error(st.error);
    onProgress?.(Math.round((st.progress || 0) * 100));
    if (st.done && st.url) return st.url as string;
  }
  throw new Error("Render timed out — try again.");
}

// Shape handed to the Remotion HD-video renderer (matches remotion/src/types.ts).
export interface RemotionRecap {
  roundLabel: string; origin?: string; zoneMode?: string; hidingStation?: string; hidingStationId?: string;
  teamName: string; members: string[]; hiders: string[];
  outcome: "caught" | "escaped"; winner?: string;
  moments: { kind: string; text: string; runTimeSec: number; count?: number; ids?: string[]; photoUrl?: string }[];
  trail?: { lat: number; lng: number; t?: number }[];
  elimTimeline?: { ids: string[]; t: number }[];
  squadEnds?: { name: string; lat: number; lng: number; caught?: boolean }[];
}

// Pull the live recap data and shape THIS seeker's squad into the Remotion props.
export async function buildRemotionRecap(client: GameClient): Promise<RemotionRecap | null> {
  const raw = await client.requestRecap();
  if (!raw) return null;
  const sq = raw.squads.find((s) => s.key === client.seekerKey)
    ?? raw.squads.find((s) => s.name === raw.winner?.squadName)
    ?? raw.squads.find((s) => s.moments?.length)
    ?? raw.squads[0];
  if (!sq) return null;
  return {
    roundLabel: raw.roundLabel,
    origin: raw.origin,
    zoneMode: raw.zoneMode,
    hidingStation: raw.hidingStationId ? (STATION_BY_ID[raw.hidingStationId]?.name ?? "the hideout") : undefined,
    hidingStationId: raw.hidingStationId ?? undefined,
    teamName: client.team?.name || sq.name,
    members: sq.members,
    hiders: raw.hiders,
    outcome: raw.outcome ?? "escaped",
    winner: raw.winner?.squadName,
    moments: (sq.moments || []).map((m) => ({ kind: m.kind, text: m.text, runTimeSec: m.runTimeSec, count: m.count, ids: m.ids, photoUrl: m.photoKey ? photoUrl(m.photoKey) : undefined })),
    trail: sq.trail,                  // this squad's GPS path
    elimTimeline: sq.elimTimeline,    // the real, full elimination order (drives the map)
    squadEnds: raw.squadEnds,         // every squad's last spot (escaped-summary shot)
  };
}

export interface RecapStep { ids: string[]; text: string; by?: string; gps?: { lat: number; lng: number } }
export interface RecapSquad { name: string; members: string[]; steps: RecapStep[] }
export interface RecapData {
  roundLabel: string;                       // e.g. "Round 1"
  origin?: string;                          // start-point label
  hidingStation?: { id?: string; name: string };
  hiders: string[];                         // hiding pod members
  winner?: { squadName: string; members: string[] }; // squad that caught them
  squads: RecapSquad[];
}

const W = 440, H = 580;
const PAD = { top: 118, bottom: 132, left: 34, right: 34 };
// vintage-MTA palette
const C = {
  bg1: "#1d241f", bg2: "#141a16", paper: "#E9DCBE", ink: "#231C12",
  active: "#39c06a", activeDim: "#1f6b3e", elim: "#6b6f69", elimEdge: "#4a4e49",
  white: "#ffffff", red: "#EE352E", yellow: "#FCCC0A", blue: "#2f7bdb",
  orange: "#FF6319", line: "#FCCC0A", pin: "#EE352E", muted: "#b7ad95",
};

type Proj = (lat: number, lng: number) => { x: number; y: number };
function makeProjector(): Proj {
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
  for (const s of STATIONS) { minLat = Math.min(minLat, s.lat); maxLat = Math.max(maxLat, s.lat); minLng = Math.min(minLng, s.lng); maxLng = Math.max(maxLng, s.lng); }
  const cos = Math.cos(((minLat + maxLat) / 2) * Math.PI / 180);
  const geoW = (maxLng - minLng) * cos, geoH = (maxLat - minLat);
  const mapW = W - PAD.left - PAD.right, mapH = H - PAD.top - PAD.bottom;
  const scale = Math.min(mapW / geoW, mapH / geoH);
  const offX = PAD.left + (mapW - geoW * scale) / 2;
  const offY = PAD.top + (mapH - geoH * scale) / 2;
  return (lat, lng) => ({ x: offX + (lng - minLng) * cos * scale, y: offY + (maxLat - lat) * scale });
}

interface Frame { ms: number; draw: (ctx: CanvasRenderingContext2D) => void }

// ---- drawing helpers -------------------------------------------------------
function bg(ctx: CanvasRenderingContext2D) {
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, C.bg1); g.addColorStop(1, C.bg2);
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
}
function dots(ctx: CanvasRenderingContext2D, project: Proj, elim: Set<string>) {
  for (const s of STATIONS) {
    const { x, y } = project(s.lat, s.lng);
    const gone = elim.has(s.id);
    ctx.beginPath(); ctx.arc(x, y, gone ? 2.4 : 3.2, 0, 7);
    ctx.fillStyle = gone ? C.elim : C.active;
    ctx.globalAlpha = gone ? 0.5 : 1;
    ctx.fill(); ctx.globalAlpha = 1;
    if (!gone) { ctx.lineWidth = 1; ctx.strokeStyle = C.activeDim; ctx.stroke(); }
  }
}
function star(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string) {
  ctx.save(); ctx.translate(x, y); ctx.beginPath();
  for (let i = 0; i < 10; i++) { const rad = i % 2 ? r * 0.45 : r; const a = (Math.PI / 5) * i - Math.PI / 2; ctx.lineTo(Math.cos(a) * rad, Math.sin(a) * rad); }
  ctx.closePath(); ctx.fillStyle = color; ctx.fill();
  ctx.lineWidth = 1.5; ctx.strokeStyle = C.ink; ctx.stroke(); ctx.restore();
}
function trail(ctx: CanvasRenderingContext2D, project: Proj, pts: { lat: number; lng: number }[]) {
  if (pts.length < 1) return;
  const xy = pts.map((p) => project(p.lat, p.lng));
  if (xy.length > 1) {
    ctx.beginPath(); ctx.moveTo(xy[0].x, xy[0].y);
    for (let i = 1; i < xy.length; i++) ctx.lineTo(xy[i].x, xy[i].y);
    ctx.lineWidth = 2.4; ctx.strokeStyle = C.line; ctx.setLineDash([5, 4]); ctx.stroke(); ctx.setLineDash([]);
  }
  xy.forEach((p, i) => {
    ctx.beginPath(); ctx.arc(p.x, p.y, i === xy.length - 1 ? 5 : 3.5, 0, 7);
    ctx.fillStyle = C.pin; ctx.fill(); ctx.lineWidth = 1.5; ctx.strokeStyle = C.white; ctx.stroke();
  });
}
function textC(ctx: CanvasRenderingContext2D, t: string, x: number, y: number, font: string, color: string, align: CanvasTextAlign = "center") {
  ctx.font = font; ctx.fillStyle = color; ctx.textAlign = align; ctx.textBaseline = "middle"; ctx.fillText(t, x, y);
}
function pill(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, color: string) {
  const r = h / 2; ctx.beginPath();
  ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath(); ctx.fillStyle = color; ctx.fill();
}
const FB = "700 16px Oswald, Arial, sans-serif";
const trunc = (s: string, n: number) => (s.length > n ? s.slice(0, n - 1) + "…" : s);

// Top banner (round / squad heading) + bottom caption.
function topBanner(ctx: CanvasRenderingContext2D, title: string, sub: string) {
  textC(ctx, title, W / 2, 38, "800 22px Oswald, Arial, sans-serif", C.yellow);
  if (sub) textC(ctx, trunc(sub, 46), W / 2, 64, "600 13px Arial, sans-serif", C.muted);
  // signage dots
  [C.red, C.yellow, C.active, C.blue, C.orange].forEach((col, i) => { ctx.beginPath(); ctx.arc(W / 2 - 28 + i * 14, 86, 3.5, 0, 7); ctx.fillStyle = col; ctx.fill(); });
}
function bottomCaption(ctx: CanvasRenderingContext2D, members: string, action: string) {
  pill(ctx, 24, H - 96, W - 48, 30, "rgba(233,220,190,0.92)");
  textC(ctx, trunc(members, 52), W / 2, H - 81, FB, C.ink);
  if (action) textC(ctx, trunc(action, 58), W / 2, H - 46, "600 12.5px Arial, sans-serif", C.paper);
}

// ---- frame plan ------------------------------------------------------------
function buildFrames(data: RecapData, project: Proj): Frame[] {
  const frames: Frame[] = [];
  const elimAll = new Set<string>();

  // Intro
  frames.push({ ms: 1300, draw: (ctx) => {
    bg(ctx); dots(ctx, project, new Set());
    topBanner(ctx, `${data.roundLabel.toUpperCase()} · RECAP`, data.origin ? `started near ${data.origin}` : "");
    pill(ctx, 60, H - 92, W - 120, 30, "rgba(0,0,0,0.35)");
    textC(ctx, `${data.squads.length} squad${data.squads.length === 1 ? "" : "s"} on the hunt`, W / 2, H - 77, "700 14px Arial, sans-serif", C.white);
  } });

  for (const sq of data.squads) {
    const elim = new Set<string>();           // this squad's board
    const path: { lat: number; lng: number }[] = [];
    const members = sq.members.join(" · ") || sq.name;
    // squad intro
    frames.push({ ms: 950, draw: (ctx) => {
      bg(ctx); dots(ctx, project, new Set());
      topBanner(ctx, sq.name.toUpperCase(), "researching the board");
      bottomCaption(ctx, members, "");
    } });
    // sample steps down to a manageable count
    const steps = sampleSteps(sq.steps, 9);
    for (const st of steps) {
      st.ids.forEach((id) => { if (STATION_BY_ID[id]) { elim.add(id); elimAll.add(id); } });
      if (st.gps) path.push(st.gps);
      const snapElim = new Set(elim), snapPath = path.slice();
      const remaining = STATIONS.length - snapElim.size;
      frames.push({ ms: 640, draw: (ctx) => {
        bg(ctx); dots(ctx, project, snapElim); trail(ctx, project, snapPath);
        topBanner(ctx, sq.name.toUpperCase(), `${remaining} stations left`);
        bottomCaption(ctx, members, st.text);
      } });
    }
    // hold the squad's final board
    frames.push({ ms: 700, draw: (ctx) => {
      bg(ctx); dots(ctx, project, new Set(elim)); trail(ctx, project, path.slice());
      topBanner(ctx, sq.name.toUpperCase(), `narrowed to ${STATIONS.length - elim.size}`);
      bottomCaption(ctx, members, "");
    } });
  }

  // Ending card — who caught the pod
  const hideXY = data.hidingStation?.id && STATION_BY_ID[data.hidingStation.id]
    ? project(STATION_BY_ID[data.hidingStation.id].lat, STATION_BY_ID[data.hidingStation.id].lng) : null;
  const ending = (ctx: CanvasRenderingContext2D, big: boolean) => {
    bg(ctx); ctx.globalAlpha = 0.5; dots(ctx, project, new Set(elimAll)); ctx.globalAlpha = 1;
    if (hideXY) { star(ctx, hideXY.x, hideXY.y, big ? 16 : 12, C.yellow); }
    textC(ctx, "🎉  CAUGHT!", W / 2, 150, "800 30px Oswald, Arial, sans-serif", C.yellow);
    if (data.hidingStation) textC(ctx, trunc(`hiding at ${data.hidingStation.name}`, 40), W / 2, 188, "700 15px Arial, sans-serif", C.white);
    pill(ctx, 30, 250, W - 60, 92, "rgba(233,220,190,0.95)");
    textC(ctx, data.winner ? `Found by ${data.winner.squadName}` : "Round over", W / 2, 278, "800 18px Oswald, Arial, sans-serif", C.ink);
    textC(ctx, trunc((data.winner?.members || []).join(" · "), 46), W / 2, 306, FB, "#5a4e30");
    if (data.hiders.length) textC(ctx, trunc(`Hiders: ${data.hiders.join(" · ")}`, 50), W / 2, 372, "600 13px Arial, sans-serif", C.muted);
    textC(ctx, "JET LAG NYC", W / 2, H - 40, "800 13px Oswald, Arial, sans-serif", C.muted);
  };
  frames.push({ ms: 1400, draw: (ctx) => ending(ctx, false) });
  frames.push({ ms: 1800, draw: (ctx) => ending(ctx, true) });
  return frames;
}

function sampleSteps(steps: RecapStep[], max: number): RecapStep[] {
  if (steps.length <= max) return steps;
  const out: RecapStep[] = []; const stride = steps.length / max;
  for (let i = 0; i < max; i++) out.push(steps[Math.floor(i * stride)]);
  out[out.length - 1] = steps[steps.length - 1]; // always include the last
  return out;
}

// ---- encode ----------------------------------------------------------------
export async function generateRecapGif(data: RecapData, onProgress?: (pct: number) => void): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
  const project = makeProjector();
  const frames = buildFrames(data, project);
  const gif = GIFEncoder();
  for (let i = 0; i < frames.length; i++) {
    frames[i].draw(ctx);
    const rgba = ctx.getImageData(0, 0, W, H).data;
    const palette = quantize(rgba, 128);
    const index = applyPalette(rgba, palette);
    gif.writeFrame(index, W, H, { palette, delay: frames[i].ms });
    onProgress?.((i + 1) / frames.length);
    if (i % 4 === 0) await new Promise((r) => setTimeout(r, 0)); // yield to keep UI alive
  }
  gif.finish();
  return new Blob([new Uint8Array(gif.bytesView())], { type: "image/gif" });
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

// Request a round's recap data (team leaders' canonical boards) from the worker,
// render the GIF, and download it. Returns false if there's nothing to recap.
export async function downloadRoundRecap(client: GameClient, round?: number): Promise<boolean> {
  const raw = await client.requestRecap(round);
  if (!raw || !raw.squads?.some((s) => s.steps.length)) return false;
  const data: RecapData = {
    roundLabel: raw.roundLabel,
    origin: raw.origin,
    hidingStation: raw.hidingStationId ? { id: raw.hidingStationId, name: STATION_BY_ID[raw.hidingStationId]?.name ?? "the hideout" } : undefined,
    hiders: raw.hiders || [],
    winner: raw.winner || undefined,
    squads: raw.squads.map((s) => ({ name: s.name, members: s.members, steps: s.steps })),
  };
  const blob = await generateRecapGif(data);
  downloadBlob(blob, `jetlag-${(raw.roundLabel || "recap").toLowerCase().replace(/\s+/g, "-")}.gif`);
  return true;
}
